/**
 * RejectReasonBanner — optional, non-blocking reject-reason prompt.
 *
 * Phase 3.6 (UX simplification): swipe kiri langsung me-log reject dan
 * mengganti kartu — tidak ada lagi bottom sheet wajib. Banner ini muncul
 * setelahnya sebagai prompt KECIL yang bisa diabaikan:
 *  - Tap chip alasan → kirim alasan ke server (backfill), banner tutup.
 *  - Tap ✕ / swipe kartu berikutnya → banner tutup tanpa mengirim apa pun.
 *
 * Tidak ada free text ("Lainnya") — banner harus tetap satu-tap.
 */

import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { Brand, Radii, Spacing } from '@/constants/theme';
import type { RejectReason } from '@funpaper/shared';

export type RejectReasonBannerProps = {
  /** Nama worksheet yang baru ditolak (untuk konteks copy). */
  cardName: string;
  /** User memilih satu alasan. */
  onPick: (reason: RejectReason) => void;
  /** User menutup banner tanpa memilih. */
  onDismiss: () => void;
  /** Disable reason actions while the optional reason is being saved. */
  loading?: boolean;
};

type ReasonOption = {
  value: RejectReason;
  label: string;
};

/** Text-only chips (Fase 3 redesign) — label sudah cukup jelas tanpa ikon. */
const REASONS: readonly ReasonOption[] = [
  { value: 'tema', label: 'Tema tidak menarik' },
  { value: 'difficulty_easy', label: 'Terlalu mudah' },
  { value: 'difficulty_hard', label: 'Terlalu sulit' },
  { value: 'boring', label: 'Anak bosan' },
  { value: 'duplicate', label: 'Sudah pernah' },
  { value: 'other', label: 'Lainnya' },
];

export function RejectReasonBanner({ cardName, onPick, onDismiss, loading = false }: RejectReasonBannerProps) {
  return (
    <View style={styles.banner}>
      <View style={styles.headerRow}>
        <ThemedText style={styles.title} numberOfLines={1}>
          Kenapa &ldquo;{cardName}&rdquo; tidak cocok?
        </ThemedText>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Tutup tanpa memilih alasan"
          onPress={onDismiss}
          disabled={loading}
          style={({ pressed }) => [styles.closeBtn, pressed && styles.closeBtnPressed]}
        >
          <Text style={styles.closeBtnText}>✕</Text>
        </Pressable>
      </View>
      <ThemedText type="small" themeColor="textSecondary">
        Opsional — bantu kami pilih yang lebih pas besok.
      </ThemedText>
      {loading ? (
        <View style={styles.loadingRow}>
          <ActivityIndicator size="small" color={Brand.primary} />
          <Text style={styles.loadingText}>Menyimpan masukan…</Text>
        </View>
      ) : null}
      <View style={styles.chips}>
        {REASONS.map((r) => (
          <Pressable
            key={r.value}
            accessibilityRole="button"
            accessibilityLabel={`Alasan: ${r.label}`}
            onPress={() => onPick(r.value)}
            disabled={loading}
            style={({ pressed }) => [styles.chip, loading && styles.disabled, pressed && styles.chipPressed]}
          >
            <Text style={styles.chipLabel}>{r.label}</Text>
          </Pressable>
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  banner: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e5e5',
    borderRadius: Radii.medium,
    padding: Spacing.three,
    gap: Spacing.two,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.two,
  },
  title: {
    flex: 1,
    fontSize: 14,
    fontWeight: '700',
    color: Brand.primary,
  },
  closeBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f3ee',
  },
  closeBtnPressed: {
    opacity: 0.6,
  },
  closeBtnText: {
    fontSize: 13,
    color: '#6b6573',
    fontWeight: '700',
  },
  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.two,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.two,
  },
  loadingText: {
    color: Brand.primary,
    fontSize: 12,
    fontWeight: '600',
  },
  disabled: {
    opacity: 0.5,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#fcfbf8',
    borderWidth: 1,
    borderColor: '#e5e5e5',
    borderRadius: Radii.pill,
    paddingVertical: 6,
    paddingHorizontal: Spacing.two,
  },
  chipPressed: {
    backgroundColor: '#f5f0fa',
    borderColor: Brand.primary,
  },
  chipLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#201c23',
  },
});
